/*
 * The live channel — ONE hibernated WebSocket to the ai-worker's org room, for EVERY Vitesse UI.
 *
 * This lived in the AI console, which meant the console was the only surface where a config change
 * made elsewhere arrived without a reload — the gateway app and the fleet manager ran their AI
 * sessions on whatever policy was true when their rail last opened. That is backwards: the
 * platform rule is cross-tab consistency automatic, server broadcasts, clients listen — and the
 * broadcasting half was already running for everyone. This is the listening half, in the shared
 * layer, so every surface gets it by construction rather than by porting.
 *
 * Views `subscribe(topic, onInvalidate)` for the surface they're showing; on a RoomFrame INVALIDATE
 * the manager fires their callback (which re-fetches the unary snapshot). The subscription scope IS
 * the set of currently-subscribed topics (viewport-scoping): the last view to drop a topic narrows
 * the scope and the room stops sending it. Reconnects with the seq cursor; the session token is
 * refreshed on each connect (it rides the WS subprotocol — browsers cannot set headers on a WS
 * handshake). The browser holds zero authoritative state — this only says "re-fetch X".
 *
 * CONFIGURED BY `configureAiPlane`, like the driver and for the same reason: the backend origin and
 * the token are the app's two facts, and a channel configured separately is a channel one app
 * forgets. Configuration also arms the ONE standing subscription every surface needs — CONFIG →
 * `aiConfigEvents` — so a rule flipped anywhere reaches every open session's next handshake with no
 * per-app wiring at all. Debounced, because a save can land several invalidations back to back and
 * each dispatch costs a full handshake.
 */
import { create, toBinary, fromBinary } from '@bufbuild/protobuf';
import { SubscribeScopeSchema, RoomFrameSchema, RoomFrame_Kind, LiveTopic } from '@vitesse/proto/vitesse/ai/v1/live_pb.js';
import { aiConfigEvents } from './manager';
export { LiveTopic };
class LiveManager {
    constructor() {
        this.connecting = false;
        this.cbs = new Map();
        this.seq = 0n; // lastSyncId cursor for resume
        this.backoff = 500;
        this.wsUrl = '';
        this.token = async () => null;
    }
    /** Where the room lives and how to authenticate — the app's facts, handed over at boot. A
     *  subscription made before configuration simply waits; this kicks the connect for it. */
    configure(wsUrl, token) {
        this.wsUrl = wsUrl;
        this.token = token;
        if (this.ws) {
            // Reconfigured mid-life: the open socket is bound to the OLD origin/token, and connect()
            // would see it as healthy and never open against the new facts. Drop it silently — its
            // handlers detached so its close doesn't schedule a backoff reconnect or clobber the
            // socket connect() opens right below.
            const old = this.ws;
            this.ws = undefined;
            old.onopen = old.onmessage = old.onclose = old.onerror = null;
            old.close();
        }
        void this.connect();
    }
    /** Register interest in a topic → re-fetch callback. Returns an unsubscribe fn. The first callback
     *  for a topic widens the scope; the last one to leave narrows it (trash-on-navigate). */
    subscribe(topic, cb) {
        let set = this.cbs.get(topic);
        const isNew = !set;
        if (!set) {
            set = new Set();
            this.cbs.set(topic, set);
        }
        set.add(cb);
        void this.connect();
        if (isNew)
            this.sendScope();
        return () => {
            const s = this.cbs.get(topic);
            if (!s)
                return;
            s.delete(cb);
            if (s.size === 0) {
                this.cbs.delete(topic);
                this.sendScope();
            }
        };
    }
    async connect() {
        if (!this.wsUrl || this.connecting || (this.ws && this.ws.readyState <= WebSocket.OPEN))
            return;
        this.connecting = true;
        let ws;
        try {
            const token = await this.token();
            if (!token || this.cbs.size === 0) {
                this.connecting = false;
                return;
            }
            // The token rides the subprotocol: new WebSocket(url, ['bearer', <jwt>]).
            ws = new WebSocket(this.wsUrl, ['bearer', token]);
        }
        catch {
            // A token mint that rejects or a constructor that throws must not leave `connecting`
            // pinned — every later connect() would bail on it and the channel would silently stop
            // reconnecting for the life of the page. Back off and try again like any other drop.
            this.connecting = false;
            this.reconnect();
            return;
        }
        ws.binaryType = 'arraybuffer';
        ws.onopen = () => {
            this.connecting = false;
            this.backoff = 500;
            this.sendScope();
        };
        ws.onmessage = (e) => this.onFrame(new Uint8Array(e.data));
        ws.onclose = () => {
            this.ws = undefined;
            this.connecting = false;
            this.reconnect();
        };
        ws.onerror = () => {
            try {
                ws.close();
            }
            catch {
                /* already closing */
            }
        };
        this.ws = ws;
    }
    sendScope() {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN)
            return;
        const topics = [...this.cbs.keys()];
        this.ws.send(toBinary(SubscribeScopeSchema, create(SubscribeScopeSchema, { topics, sinceSeq: this.seq })));
    }
    onFrame(bytes) {
        const f = fromBinary(RoomFrameSchema, bytes);
        this.seq = f.seq;
        if (f.kind === RoomFrame_Kind.INVALIDATE) {
            for (const cb of this.cbs.get(f.topic) ?? [])
                cb();
        }
    }
    reconnect() {
        if (this.cbs.size === 0)
            return; // nothing on screen → stay closed until a subscribe
        setTimeout(() => void this.connect(), this.backoff);
        this.backoff = Math.min(this.backoff * 2, 10_000);
    }
}
export const live = new LiveManager();
let configBridged = false;
/** Called from `configureAiPlane`. Split out so the index stays a wiring table. */
export function configureLive(aiUrl, token) {
    // '' means "same origin" everywhere else on the plane; the WS constructor alone needs it spelled
    // out, and only a browser has an origin to spell.
    const origin = aiUrl || (typeof location !== 'undefined' ? location.origin : '');
    if (!origin)
        return; // not a browser (a test importing the module) — nothing to connect
    live.configure(origin.replace(/^http/, 'ws') + '/ws', token);
    if (configBridged)
        return;
    configBridged = true;
    let pending;
    live.subscribe(LiveTopic.CONFIG, () => {
        clearTimeout(pending);
        pending = setTimeout(() => aiConfigEvents.dispatchEvent(new Event('change')), 300);
    });
}
//# sourceMappingURL=live.js.map